import { alpha, Box, ButtonBase, Divider, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { tileSliceDetailsShownState } from '~/state/atoms/viewerAtoms';
import { ChangeSource, RenderAdapter } from '../adapters/types';
import { READOUT, READOUT_DIM, READOUT_FONT } from '../chrome';
import { Plane } from '../layout/types';
import {
    planeName,
    samplingRows,
    SliceIndicator,
    sliceNumberingLine,
} from './tileViewModel';
import { stopTitleBarPress, TitlePlaneLabel } from './TileTitleBar';
import { useSliceIndicator } from './useSliceIndicator';

/** How far the details keep from the edge of the element they stay in, in CSS pixels. */
const DETAILS_EDGE_GAP_PX = 8;

/** Labelled values in two columns: the label left, the value right. */
const ROWS_SX = {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    columnGap: 2,
    rowGap: 0.5,
    alignItems: 'baseline',
};

/**
 * The hairline between the parts of the details, drawn from the text
 * colour so it shows on the tooltip's lifted ground.
 */
const DIVIDER_SX = {
    borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.16),
};

/** A line that explains the values above it, in the small muted type. */
const NOTE_SX = { color: READOUT_DIM, fontSize: 10, lineHeight: 1.3 };

/** One labelled value of the details, its value in the readout type. */
function DetailsRow({
    label,
    value,
    emphasised = false,
    valueTestId = 'slice-details-value',
}: {
    label: string;
    value: string;
    emphasised?: boolean;
    valueTestId?: string;
}) {
    return (
        <>
            <Box
                component="span"
                data-testid="slice-details-label"
                sx={{ color: READOUT_DIM }}
            >
                {label}
            </Box>
            <Box
                component="span"
                data-testid={valueTestId}
                sx={{
                    fontFamily: READOUT_FONT,
                    textAlign: 'right',
                    color: READOUT,
                    fontWeight: emphasised ? 700 : 400,
                }}
            >
                {value}
            </Box>
        </>
    );
}

/**
 * The slice details of a tile in three parts parted by hairlines: the
 * slice the view shows of how many, with which way the slices are
 * numbered through the patient beneath it; how the image is sampled; and
 * what a press of the control does. A plane that crosses the slices names
 * no slice, so the first part is left out.
 */
function SliceDetails({
    indicator,
    shown,
}: {
    indicator: SliceIndicator;
    shown: boolean;
}) {
    const intl = useIntl();
    return (
        <Box
            data-testid="slice-details"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                minWidth: 200,
                py: 0.5,
                fontSize: 11,
                lineHeight: 1.2,
            }}
        >
            {indicator.kind === 'slice' ? (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box sx={ROWS_SX}>
                            <DetailsRow
                                label={intl.formatMessage({
                                    id: 'slice.details.slice',
                                })}
                                value={`${indicator.index} / ${indicator.count}`}
                                valueTestId="slice-details-index"
                                emphasised
                            />
                        </Box>
                        <Box
                            component="span"
                            data-testid="slice-details-numbering"
                            sx={NOTE_SX}
                        >
                            {sliceNumberingLine(intl, indicator.numbering)}
                        </Box>
                    </Box>
                    <Divider sx={DIVIDER_SX} />
                </>
            ) : null}
            <Box sx={ROWS_SX}>
                {samplingRows(intl, indicator.sampling).map(
                    ({ label, value }) => (
                        <DetailsRow key={label} label={label} value={value} />
                    )
                )}
            </Box>
            <Divider sx={DIVIDER_SX} />
            <Box component="span" data-testid="slice-details-hint" sx={NOTE_SX}>
                {intl.formatMessage({
                    id: shown ? 'slice.details.hide' : 'slice.details.show',
                })}
            </Box>
        </Box>
    );
}

/**
 * The name of the plane a tile draws, with an info mark, as the control
 * for the tile's slice details. Hover or keyboard focus shows them; a
 * press shows the slice number and spacing in the title bar of every
 * tile, or hides them there again, and the choice is kept.
 */
export default function SliceDetailsButton({
    plane,
    renderer,
    frameSource,
    boundary,
}: {
    plane: Plane;
    renderer: RenderAdapter;
    frameSource: ChangeSource;
    /**
     * The element whose left and right edges the details keep
     * {@link DETAILS_EDGE_GAP_PX} inside, or null to keep them inside the
     * window.
     */
    boundary: HTMLElement | null;
}) {
    const intl = useIntl();
    const indicator = useSliceIndicator(renderer, frameSource);
    const [shown, setShown] = useRecoilState(tileSliceDetailsShownState);
    const popperModifiers = useMemo(
        () => [
            {
                name: 'preventOverflow',
                options: {
                    padding: DETAILS_EDGE_GAP_PX,
                    ...(boundary ? { boundary } : {}),
                },
            },
        ],
        [boundary]
    );
    return (
        <Tooltip
            describeChild
            placement="bottom"
            slotProps={{ popper: { modifiers: popperModifiers } }}
            title={
                indicator ? (
                    <SliceDetails indicator={indicator} shown={shown} />
                ) : (
                    ''
                )
            }
        >
            <ButtonBase
                data-testid="slice-details-toggle"
                aria-pressed={shown}
                aria-label={intl.formatMessage(
                    { id: 'slice.details.toggle' },
                    { plane: planeName(intl, plane) }
                )}
                onClick={() => setShown((current) => !current)}
                onPointerDown={stopTitleBarPress}
                onMouseDown={stopTitleBarPress}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: 0,
                    px: 0.25,
                    borderRadius: 0.5,
                    color: shown ? READOUT : READOUT_DIM,
                    '& [data-testid="tile-plane"]': { color: 'inherit' },
                    '&:hover': { color: READOUT },
                    '&.Mui-focusVisible': {
                        outline: `1px solid ${READOUT}`,
                    },
                }}
            >
                <TitlePlaneLabel plane={plane} />
                <Info size={12} aria-hidden />
            </ButtonBase>
        </Tooltip>
    );
}
